
import { ChartCard } from './ChartCard';
import { AdPerformanceCard, type OrigemMetaClicada } from './AdPerformanceCard';
import { WeeklyAnalysisCard } from './WeeklyAnalysisCard';
import { CustomLinksCard } from './CustomLinksCard';
import { DashboardMetrics } from '@/hooks/dashboard/types';
import { Lead } from '@/hooks/useSupabaseLeads';
import type { ContextoDashboard } from '@/features/dashboard/data/dashboardContracts';
import type { FiltroSemanaDashboard } from '@/features/dashboard/data/dashboardSeries';

/**
 * Componente da seção de gráficos do Dashboard
 * 
 * O que faz:
 * - Renderiza os gráficos de origem das conversas e análise semanal
 * - Exibe a performance de anúncios
 * - NOVO: Exibe performance de links personalizados
 * - Processa dados de leads para diferentes visualizações
 * - Passa dados admin para componentes que necessitam
 * 
 * Onde é usado:
 * - Componente Dashboard principal
 * 
 * Como se conecta:
 * - Recebe dados processados do Supabase via props
 * - Passa dados brutos filtrados para os gráficos
 * - Integra com modais de drill-down via callbacks
 */

interface DashboardChartsProps {
  dashboardData: DashboardMetrics | null;
  filteredRawLeads: Lead[];
  onAdClick?: (adName: string) => void;
  // Clique numa linha das abas Conjunto/Campanha do card de anúncios (abre os leads dela).
  onOrigemMetaClick?: (origem: OrigemMetaClicada) => void;
  onLinkClick?: (linkName: string) => void;
  adminMode?: boolean;
  targetClinicaId?: string;
  // Necessário para o ChartCard gerar todos os dias do período selecionado
  startDate?: Date | null;
  endDate?: Date | null;
  isUpdating?: boolean; // Indica se os dados estão sendo atualizados suavemente
  contexto?: ContextoDashboard;
  filtroSemanal?: FiltroSemanaDashboard;
  onFiltroSemanalChange?: (filtro: FiltroSemanaDashboard) => void;
}

export const DashboardCharts = ({
  dashboardData,
  filteredRawLeads,
  onAdClick,
  onOrigemMetaClick,
  onLinkClick,
  adminMode,
  targetClinicaId,
  startDate,
  endDate,
  isUpdating = false,
  contexto,
  filtroSemanal,
  onFiltroSemanalChange,
}: DashboardChartsProps) => {
  // Processamento de dados de performance de anúncios
  const adPerformanceData = dashboardData?.leadsPorAnuncio || [];

  // NOVO: Processamento de dados de performance de links personalizados
  const linksPerformanceData = dashboardData?.leadsPorLink || [];
  const resumo = dashboardData?.resumo;

  return (
    // Grid responsivo: 1 coluna no mobile/tablet, 2 colunas em desktop
    // gap-4 no mobile para economizar espaço, gap-6 em telas maiores
    <div className={`grid grid-cols-1 lg:grid-cols-2 gap-3 sm:gap-4 transition-opacity duration-500 ${isUpdating ? 'opacity-70' : 'opacity-100'}`}>
      {/* Gráfico de Origem das Conversas — startDate/endDate garantem todos os dias do período */}
      <ChartCard
        title="Origem das Conversas"
        description="Total de leads por dia com filtros por origem"
        data={[]} // Será processado internamente pelo ChartCard
        type="bar"
        showFilter={true}
        rawLeadsData={filteredRawLeads}
        serieResumo={resumo?.serie}
        passoDias={resumo?.passoDias}
        startDate={startDate}
        endDate={endDate}
      />

      {/* Card de Performance de Anúncios */}
      <AdPerformanceCard
        data={adPerformanceData}
        resumoAnuncios={resumo?.anuncios}
        onAdClick={onAdClick}
        onOrigemMetaClick={onOrigemMetaClick}
        adminMode={adminMode}
        targetClinicaId={targetClinicaId}
        contexto={contexto}
        isUpdating={isUpdating}
      />

      {/* NOVO: Card de Performance de Links Personalizados */}
      <CustomLinksCard
        data={linksPerformanceData}
        onLinkClick={onLinkClick}
        adminMode={adminMode}
        targetClinicaId={targetClinicaId}
      />

      {/* Gráfico de Análise por Dia da Semana */}
      <WeeklyAnalysisCard
        leads={filteredRawLeads}
        semanaResumo={resumo?.semana}
        title="Distribuição por Dia da Semana"
        description="Análise de leads por dia da semana"
        filtro={filtroSemanal}
        onFiltroChange={onFiltroSemanalChange}
      />
    </div>
  );
};
